import { enableProdMode, provideZoneChangeDetection } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';

// Capture deep link BEFORE Angular bootstraps. The OAuth library navigates to
// '/' after SSO completes, which discards the originally requested route.
// We save it here so app.component.loadApp() can restore it post-auth.
try {
  const path = window.location.pathname || '/';
  const isCallback = path.startsWith('/callback');
  const isLogout = path.startsWith('/logout');
  const isRoot = path === '/' || path === '' || path === '/home';
  if (!isCallback && !isLogout && !isRoot) {
    sessionStorage.setItem('mmt.deepLink', path + window.location.search);
  }
} catch { /* sessionStorage unavailable */ }

if (environment.production) {
  enableProdMode();
}

/**
 * Mark the app as serverless BEFORE Angular bootstraps.
 *
 * The Compass @stg-js-ngx-core/oauth APP_INITIALIZER decides between the serverless
 * flow and the legacy (Node server-side) flow at init time via:
 *     isServerless = serverless || !!(serverlessConfig || window.serverlessOAuthConfig)
 * OauthSessionModule.forRoot() is called with no args, so unless window.serverlessOAuthConfig
 * is set here the library falls into the LEGACY branch and fires
 * GET /ui/api/users/oauthProfile (404 in the serverless architecture, and the wrong
 * response shape). Configuring serverless late in AppComponent.ngOnInit is too late ? the
 * initializer has already run. We fetch appsettings.json (same file AppConfigService loads)
 * and publish the serverless config on window so the library takes the serverless branch.
 */
async function bootstrap(): Promise<void> {
  try {
    const res = await fetch('/appsettings.json', { headers: { Accept: 'application/json' } });
    if (res.ok) {
      const config: any = await res.json();
      (window as any).serverlessOAuthConfig = {
        serverless: true,
        clientId: config.clientId,
        rbacEnabled: true,
        rbacAppName: config.rbacAppName,
        revalidateAfterMin: 30,
        sessionExpirationAlertInMin: 3,
        longestTimeToLiveHours: 8,
        autoLogoutOnSessionExpired: true,
        logoutUrl: config.baseUrls?.logout,
        environmentConfig: config
      };
    }
  } catch {
    // If appsettings cannot be pre-fetched, fall through: AppComponent.ngOnInit still
    // configures serverless handling. The initializer's legacy profile call may 404, but
    // bootstrapping must not be blocked by this optimization.
  }

  // Angular 22 defaults NgModule apps to zoneless (NoopNgZone). This app relies entirely on
  // zone.js auto-ticking, so opt back in explicitly - bootstrapModule is the only place it's accepted.
  await platformBrowserDynamic().bootstrapModule(AppModule, {
    applicationProviders: [provideZoneChangeDetection()]
  });
}

bootstrap().catch(err => console.error(err));
